import React, { useMemo } from 'react';
import { APITypes, MedicalDataTypes, Dynamo } from '../../../../primary-record-types';
import { getIntl } from '../../../../utils/intl';
import { FastField } from 'formik';
import { getDateFormatValidator, required } from '../../../../utils/validators';
import ImagesField, { formValueImagesToResourceImageUpdates, resourceImagesToFormValueImages } from '../ImagesField';
import { FormValueImage } from '../ImagesField/types';
import { handleFormikSubmit } from '../../../../utils/handleFormikSubmit';
import useRecordEvent from '../../../../hooks/useRecordEvent';
import VaccineTypeInput from './VaccineTypeInput';
import { CenteredFormRow, Form, SplitFormRow } from '../../../v2BaseComponents/Form';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { FormStack } from '../../../v2BaseComponents/Stack';

const intl = getIntl('VaccinationsForm', {
    save: 'Save',
    vaccination: 'Vaccination Type',
    notes: 'Additional Information',
    dateTime: 'Date',
    VaccinationType: 'Vaccination Type',
    address1: 'Address 1',
    address2: 'Address 2',
    city: 'City',
    state: 'State',
    zipCode: 'Zip Code',
    selectCareTeamProvider: 'Select Care Team Provider',
    provider: 'Provider',
    providerName: 'Provider Name',
});

export type VaccinationFormOwnProps = {
    isLoading: boolean;
    startDateTime?: string;
    defaultResource?: MedicalDataTypes.PrimaryRecordVaccination;
    defaultImages?: Dynamo.ResourceImage[];
    onSubmit: (data: APITypes.medicalDataService.UpdateResourceBody<MedicalDataTypes.PrimaryRecordVaccination>) => void;
    isDisabled: boolean;
    profileId: string;
    resourceId?: string;
    medicalProfileId: string;
    onCancel: () => void;
};

type VaccinationResourceFormValue = MedicalDataTypes.PrimaryRecordVaccination;
type FormValue = {
    resource: VaccinationResourceFormValue;
    images: FormValueImage[];
};

const getDefaultFormValue = (startDateTime?: string): FormValue => ({
    resource: {
        id: '', //ignored
        version: '1',
        type: 'vaccination',
        vaccineType: null as any,
        dateTime: startDateTime || null,
        notes: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
    },
    images: [],
});

export type VaccinationFormProps = VaccinationFormOwnProps;

const VaccinationForm: React.FC<VaccinationFormProps> = ({
    isLoading,
    onSubmit,
    isDisabled,
    defaultImages,
    defaultResource,
    startDateTime,
    profileId,
    resourceId,
    medicalProfileId,
    onCancel,
}) => {
    const initialValues = useMemo<FormValue>(
        () =>
            defaultResource && defaultImages
                ? {
                      resource: defaultResource,
                      images: resourceImagesToFormValueImages(defaultImages),
                  }
                : getDefaultFormValue(startDateTime),
        [defaultImages, defaultResource, startDateTime],
    );
    const recordEvent = useRecordEvent();

    return (
        <Form
            actionContainerSubmit
            initialValues={initialValues}
            onSubmit={(values, helpers) => {
                onSubmit({
                    resourceUpdate: {
                        ...values.resource,
                    },
                    imageUpdates: formValueImagesToResourceImageUpdates(values.images),
                });
                handleFormikSubmit(values, helpers);
                recordEvent(!!defaultResource ? 'editVaccination' : 'createVaccination');
            }}
            onCancel={onCancel}
            cancelButtonVariant="secondary"
        >
            {({ values, setFieldValue }) => {
                return (
                    <FormStack>
                        <SplitFormRow>
                            <FastField
                                component={VaccineTypeInput}
                                name="resource.vaccineType"
                                isRequired
                                title={intl('vaccination')}
                                isDisabled={isDisabled || isLoading}
                                validate={required}
                                medicalProfileId={medicalProfileId}
                            />
                            <FastField
                                component={FormikInputs.TextArea}
                                name="resource.notes"
                                title={intl('notes')}
                                isDisabled={isDisabled || isLoading}
                            />
                        </SplitFormRow>
                        <SplitFormRow>
                            <FastField
                                name="resource.dateTime"
                                title={intl('dateTime')}
                                component={FormikInputs.DateTimePicker}
                                validate={getDateFormatValidator('MM/DD/YYYY')}
                                isDisabled={isDisabled || isLoading}
                            />
                        </SplitFormRow>
                        <CenteredFormRow>
                            <ImagesField
                                isDisabled={isDisabled || isLoading}
                                values={values.images}
                                resourceId={resourceId}
                                profileId={profileId}
                                category="vaccination"
                                setFieldValue={setFieldValue}
                            />
                        </CenteredFormRow>
                    </FormStack>
                );
            }}
        </Form>
    );
};

export default React.memo(VaccinationForm);
